import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { gsap } from "gsap";
import { 
  getTenantId, 
  isPrimaryOccupant, 
  parseArrayData,
  getCars,
  addCar,
  generateCarImage,
  getDependents,
  getCarById,
  updateCar,
  deleteCar,
  assignDependentToCar,
  unassignDependentFromCar,
  uploadImageIfNeeded,
  getCarAccessCredential,
  rotateCarAccessCredential,
} from "./TenantAuth";
import { useTenantHeader } from "./TenantLayout";
import { carMakes, carColors, getModelsForMake } from "../data/carCatalog";
import toast from "react-hot-toast";
import { confirmDialog } from "../../components/ConfirmDialog";
import * as XLSX from "xlsx";

// ─── Helpers ─────────────────────────────────────────────────
function toTitleCase(str = "") {
  return str.toLowerCase().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

function ModalShell({ modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

function Avatar({ src, name, size = 48, onClick }) {
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
}

function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay d6-topnav-preloader ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

const BLANK_CAR = { manufacturer: "", make: "", model: "", color: "", licensePlate: "", year: new Date().getFullYear() };

// ─── Occupant multi-select (with photos) ─────────────────────
function OccupantMultiSelect({ deps, value, onChange }) {
  const [open, setOpen] = useState(false);
  const idOf = (d) => d.dependentId || d.id;
  const nameOf = (d) => [d.title, d.firstName, d.lastName].filter(Boolean).join(" ");
  const selected = deps.filter((d) => value.includes(idOf(d)));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div>
      <button
        type="button"
        className="d6m-input d-flex align-items-center justify-content-between gap-2 text-start"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="d-flex align-items-center gap-2 min-w-0 flex-wrap">
          {selected.length === 0 ? (
            <span className="text-gray-500">Select occupants…</span>
          ) : (
            selected.map((d) => (
              <span key={idOf(d)} className="d6m-chip" style={{ "--dot": "#0EA84A" }}>
                {d.firstName || nameOf(d)}
              </span>
            ))
          )}
        </span>
        <i className={`ki-outline ${open ? "ki-up" : "ki-down"} fs-5 text-gray-500 flex-shrink-0`} />
      </button>

      {open && (
        <div className="d6m-list mt-2" style={{ maxHeight: 240, overflowY: "auto" }}>
          {deps.map((d) => {
            const id = idOf(d);
            const on = value.includes(id);
            return (
              <button
                key={id}
                type="button"
                className={`d6m-list-row ${on ? "active" : ""}`}
                onClick={() => toggle(id)}
              >
                <Avatar src={d.imageUrl} name={nameOf(d)} size={36} />
                <span className="min-w-0 flex-grow-1 text-start">
                  <span className="d6m-v d-block text-truncate">{nameOf(d)}</span>
                  {(d.designation || d.dependentType) && (
                    <span className="d6m-muted d-block text-truncate">{d.designation || d.dependentType}</span>
                  )}
                </span>
                <span className={`d6m-check ${on ? "on" : ""}`} aria-hidden="true" style={on ? { background: "#18181b", borderColor: "#18181b" } : undefined}>
                  <svg viewBox="0 0 24 24" style={{ opacity: on ? 1 : 0 }}><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────

function CarModal({ modalRef, mode, initial, deps, onSave, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [form, setForm] = useState(BLANK_CAR);
  const [saving, setSaving] = useState(false);
  const [assignDeps, setAssignDeps] = useState([]);
  const [otherModel, setOtherModel] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [customImageUrl, setCustomImageUrl] = useState("");

  useEffect(() => { 
    setForm(initial || BLANK_CAR); 
    setOtherModel(false);
    setCustomImageUrl(initial?.imageUrl || "");
  }, [initial]);

  const handleClose = () => {
    hide();
    onClose?.();
  };

  const field = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleGenerateAi = async () => {
    if (!form.make?.trim()) {
      toast.error("Please enter vehicle Make first.");
      return;
    }
    setGeneratingAi(true);
    try {
      const url = await generateCarImage({
        manufacturer: form.manufacturer || form.make,
        make: form.make,
        model: form.model,
        year: form.year,
        color: form.color,
      });
      if (url && typeof url === "string" && (url.startsWith("http") || url.startsWith("data:image") || url.startsWith("/"))) {
        setCustomImageUrl(url);
        setForm(p => ({ ...p, imageUrl: url }));
        toast.success("AI vehicle image generated!");
      } else {
        toast.success("Preview generated.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to generate AI car image.");
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!form.licensePlate?.trim() || !form.make?.trim()) {
      toast.error("Make and Plate Number are required."); 
      return;
    }
    setSaving(true);
    try {
      await onSave({ form, assignDeps });
      setAssignDeps([]);
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 40 }, (_, i) => currentYear + 1 - i);
  const qrData = form.licensePlate?.trim() ? form.licensePlate.trim().toUpperCase() : "PENDING";

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-car"
      title={mode === "edit" ? "Edit Vehicle Details" : "Register New Vehicle"}
      onClose={handleClose}
      closeDisabled={saving}
      width={720}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : (
              mode === "edit" ? "Update Vehicle" : "Register Car"
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSave} className="d-flex flex-column gap-6">
        {/* Left Form Column */}
        <div className="w-100">
          {mode === "add" && deps.length > 0 && (
            <Field label="Assign to Residents / Dependants (Optional)" className="mb-4" hint="Select as many people as needed.">
              <OccupantMultiSelect deps={deps} value={assignDeps} onChange={setAssignDeps} />
            </Field>
          )}

          <div className="d6m-section mt-0">
            <div className="d6m-section-head">
              <span>Vehicle Information</span>
            </div>
            <div className="row g-3">
              <Field className="col-md-6" label="Make" required>
                <select className="d6m-input" value={form.make} onChange={e => { setOtherModel(false); setForm(p => ({ ...p, make: e.target.value, model: "" })); }} required>
                  <option value="">Select make…</option>
                  {(form.make && !carMakes.includes(form.make) ? [form.make, ...carMakes] : carMakes).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>

              <Field className="col-md-6" label="Model">
                {(() => {
                  const models = getModelsForMake(form.make);
                  const custom = form.model && !models.includes(form.model);
                  const showOther = otherModel || custom;
                  return (
                    <>
                      <select
                        className="d6m-input"
                        value={showOther ? "__other" : form.model}
                        disabled={!form.make}
                        onChange={e => {
                          if (e.target.value === "__other") { setOtherModel(true); setForm(p => ({ ...p, model: "" })); }
                          else { setOtherModel(false); setForm(p => ({ ...p, model: e.target.value })); }
                        }}
                      >
                        <option value="">{form.make ? "Select model…" : "Select make first…"}</option>
                        {models.map(m => <option key={m} value={m}>{m}</option>)}
                        {form.make && <option value="__other">Other (type it in)…</option>}
                      </select>
                      {showOther && (
                        <input className="d6m-input mt-2" placeholder="Enter model" value={form.model} onChange={field("model")} />
                      )}
                    </>
                  );
                })()}
              </Field>

              <Field className="col-md-6" label="Year">
                <select className="d6m-input" value={form.year} onChange={field("year")}>
                  <option value="">Select year…</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>

              <Field className="col-md-6" label="Colour">
                <select className="d6m-input" value={form.color} onChange={field("color")}>
                  <option value="">Select color…</option>
                  {(form.color && !carColors.includes(form.color) ? [form.color, ...carColors] : carColors).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>

              <Field className="col-12" label="License Plate Number" required hint="Enter valid vehicle registration plate number.">
                <input
                  className="d6m-input"
                  placeholder="e.g. ABC-123-XY"
                  value={form.licensePlate}
                  onChange={field("licensePlate")}
                  required
                />
              </Field>
            </div>
          </div>
        </div>
        
        {/* Right Column - Visuals & QR Code */}
        <div className="w-100 d-flex flex-column gap-4">
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="d6m-name fs-8">Vehicle Appearance</span>
              {!isPrimaryOccupant() && (
                <button 
                  type="button" 
                  className="btn btn-xs btn-light-success d6-mbtn py-1 px-3 fs-9"
                  disabled={generatingAi || !form.make}
                  onClick={handleGenerateAi}
                  title="Generate realistic vehicle render using AI"
                >
                  {generatingAi ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <i className="ki-outline ki-technology-4 fs-6 me-2" />
                      AI Render
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="d6-card p-4 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-4" style={{ height: "170px" }}>
              <img 
                alt="Car Preview" 
                className="mw-100 mh-100 object-fit-contain" 
                src={customImageUrl || form.imageUrl || `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(form.make || 'Toyota')}&modelFamily=${encodeURIComponent(form.model || '')}&modelYear=${form.year}&paintId=${encodeURIComponent(form.color || '')}&angle=23&zoomType=fullscreen`} 
              />
            </div>

            <div className="d6-card p-4 border border-gray-200 bg-white">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white p-1.5 border border-gray-200 rounded-3 shadow-xs flex-shrink-0" style={{ width: "70px", height: "70px" }}>
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`} 
                    alt="QR Code" 
                    style={{ width: "100%", height: "100%", objectFit: "contain" }} 
                  />
                </div>
                <div className="d-flex flex-column text-start min-w-0">
                  <span className="text-gray-900 fw-bold fs-8 mb-0.5">Automated Gate Access</span>
                  <span className="text-success fw-bold fs-7 letter-spacing-1">{qrData}</span>
                  <span className="text-gray-500 fs-9 mt-0.5 lh-sm">
                    Linked to plate number for estate scanner logging.
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-gray-400 fs-9 text-center pt-3 mt-auto">
            Matilda Vehicle Registry • Fast-track Access
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

function CarDetailModal({ modalRef, car, deps, tenantId, onDelete, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [assignDep, setAssignDep] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(null);
  const [credential, setCredential] = useState(null);
  const [credLoading, setCredLoading] = useState(false);
  const [rotating, setRotating] = useState(false);

  const carId = car?.id;
  const initialCredential = car?.accessCredential || null;
  useEffect(() => {
    setCredential(initialCredential);
    // The access-credential endpoints are tenant-only; primary occupants keep what the car detail returned.
    if (!carId || isPrimaryOccupant()) return undefined;
    let cancelled = false;
    setCredLoading(true);
    getCarAccessCredential(carId, tenantId)
      .then((c) => { if (!cancelled && c) setCredential(c); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCredLoading(false); });
    return () => { cancelled = true; };
  }, [carId, tenantId, initialCredential]);

  const handleClose = (refresh = false) => {
    hide();
    setAssignDep("");
    onClose?.(refresh);
  };

  if (!car) {
    return (
      <ModalShell
        modalRef={modalRef}
        icon="ki-car"
        title="Vehicle Details"
        onClose={() => handleClose(false)}
        width={760}
      >
        <div className="d6m-empty">No vehicle selected.</div>
      </ModalShell>
    );
  }

  const handleAssign = async () => {
    if (!assignDep) return;
    setAssigning(true);
    try {
      await assignDependentToCar(car.id, assignDep, tenantId);
      toast.success("Occupant assigned successfully.");
      setAssignDep(""); 
      handleClose(true);
    } catch (e) { 
      toast.error(e.message); 
    } finally { 
      setAssigning(false); 
    }
  };

  const handleUnassign = async (depId) => {
    setUnassigning(depId);
    try {
      await unassignDependentFromCar(car.id, depId, tenantId);
      toast.success("Occupant removed.");
      handleClose(true);
    } catch (e) { 
      toast.error(e.message); 
    } finally { 
      setUnassigning(null); 
    }
  };

  const credCode = credential?.code || "";
  const qrSrc = credential?.qrCodeFileUrl
    || (credCode ? `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(credCode)}` : "");

  const handleRotate = async () => {
    if (credCode && !(await confirmDialog({ title: "Generate New Code", message: "Generate a new access code? The current code and printed tag will stop working at the gate.", confirmLabel: "Generate", tone: "primary" }))) return;
    setRotating(true);
    try {
      const next = await rotateCarAccessCredential(car.id, tenantId);
      // Some responses only carry a message — re-read the credential to get the new code.
      setCredential(next?.code ? next : await getCarAccessCredential(car.id, tenantId));
      toast.success(credCode ? "Access code rotated." : "Access code issued.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRotating(false);
    }
  };

  const handlePrintQR = () => {
    if (!qrSrc) return;
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Access QR Code - ${car.licensePlate}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; text-align: center; padding: 40px; color: #333; }
            .card { border: 2px dashed #0EA84A; border-radius: 16px; padding: 40px; max-width: 400px; margin: 0 auto; background: #fff; }
            h2 { margin: 0 0 10px 0; color: #111; }
            p { margin: 0 0 30px 0; color: #0EA84A; font-size: 18px; font-weight: bold; }
            .qr-container { display: inline-block; padding: 20px; border: 1px solid #eaeaea; border-radius: 12px; margin-bottom: 20px; background: #f9f9f9; }
            img { width: 220px; height: 220px; }
            .footer { font-size: 13px; color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="card">
            <h2>${car.make} ${car.model}</h2>
            <p>${car.licensePlate} • ${car.color || 'N/A'} • ${car.year || ''}</p>
            <div class="qr-container">
              <img src="${qrSrc}" alt="QR Code" />
            </div>
            <div style="font-size:22px;font-weight:700;letter-spacing:4px;">${credCode}</div>
            <div class="footer">Scan this QR code at the estate gate for automated security access.</div>
          </div>
          <script>
            window.onload = () => { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const unassigned = deps.filter(d => !(car.assignedDependents || []).some(a => a.dependentId === (d.dependentId || d.id)));

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-car"
      title={`${car.make} ${car.model} — Details`}
      onClose={() => handleClose(false)}
      width={760}
      subheader={
        <div className="px-6 py-2 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">
            {car.manufacturer || car.make} • {car.year || "—"} • {car.color || "Standard"}
          </span>
          <StatusChip color="#0EA84A">{car.licensePlate}</StatusChip>
        </div>
      }
      footer={
        <div className="d-flex align-items-center justify-content-between w-100">
          {onDelete ? (
            <button 
              type="button" 
              className="btn btn-light-danger d6-mbtn" 
              onClick={() => { handleClose(false); onDelete(null, car); }}
            >
              <i className="ki-outline ki-trash me-2.5 fs-5" />
              Remove Vehicle
            </button>
          ) : <div />}
          <button type="button" className="btn btn-light d6-mbtn" onClick={() => handleClose(false)}>
            Close
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-6">
        {/* Vehicle & Resident Info */}
        <div className="w-100">
          <div className="d6-card p-4 d-flex align-items-center justify-content-center bg-light border border-gray-200 mb-4" style={{ height: "170px" }}>
            <img
              alt="Car Preview"
              className="mw-100 mh-100 object-fit-contain"
              src={car.imageUrl || `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(car.make || '')}&modelFamily=${encodeURIComponent(car.model || '')}&modelYear=${car.year}&paintId=${encodeURIComponent(car.color || '')}&angle=23&zoomType=fullscreen`}
            />
          </div>
          <div className="d6m-person mb-5">
            <div className="min-w-0 flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <span className="d6m-name fs-5 text-truncate">{car.make} {car.model}</span>
                <span className="badge bg-light text-gray-800 fs-9 fw-bold rounded-pill px-2.5 py-0.5">{car.year}</span>
              </div>
              <div className="d6m-muted fs-8 text-truncate">{car.manufacturer} • {car.color || "No Color"}</div>
            </div>
          </div>

          <div className="d6m-section mt-0">
            <div className="d6m-section-head">
              <span>Linked Occupants ({(car.assignedDependents || []).length})</span>
            </div>
            
            {(car.assignedDependents || []).length === 0 ? (
              <div className="d6m-empty py-4">
                No occupants currently linked to this vehicle.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2 mb-4">
                {(car.assignedDependents || []).map(a => (
                  <div key={a.dependentId} className="d6-card p-3 d-flex align-items-center justify-content-between border border-gray-200">
                    <div className="d-flex align-items-center gap-3 min-w-0">
                      <Avatar src={a.imageUrl} name={a.fullName} size={38} />
                      <div className="d-flex flex-column min-w-0">
                        <span className="d6m-name fs-8 text-truncate">{a.fullName}</span>
                        <span className="d6m-muted fs-9">Linked Resident / Dependant</span>
                      </div>
                    </div>
                    <button 
                      type="button"
                      className="d6-icon-btn ms-2" 
                      disabled={unassigning === a.dependentId} 
                      onClick={() => handleUnassign(a.dependentId)}
                      title="Remove Occupant"
                    >
                      {unassigning === a.dependentId ? <span className="spinner-border spinner-border-sm" /> : <i className="ki-outline ki-trash fs-5 text-danger" />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {unassigned.length > 0 && (
              <div className="mt-4 pt-3 border-top border-gray-100">
                <label className="d-block fs-8 fw-semibold text-gray-700 mb-2">Assign Additional Occupant</label>
                <div className="d-flex gap-2">
                  <select className="d6m-input flex-grow-1" value={assignDep} onChange={e => setAssignDep(e.target.value)}>
                    <option value="">Select resident or dependant…</option>
                    {unassigned.map(d => (
                      <option key={d.dependentId || d.id} value={d.dependentId || d.id}>
                        {[d.title, d.firstName, d.lastName].filter(Boolean).join(" ")}
                      </option>
                    ))}
                  </select>
                  <button 
                    type="button"
                    className="btn btn-success d6-mbtn flex-shrink-0"
                    onClick={handleAssign} 
                    disabled={!assignDep || assigning}
                  >
                    {assigning ? <span className="spinner-border spinner-border-sm" /> : "Link User"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Visuals & QR Code */}
        <div className="w-100 d-flex flex-column gap-4">
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2 gap-2">
              <span className="d6m-name fs-8">Access QR Tag</span>
              <div className="d-flex gap-2">
                {!isPrimaryOccupant() && (
                  <button type="button" className="btn btn-xs btn-light-success d6-mbtn py-1 px-3 fs-9" onClick={handleRotate} disabled={rotating || credLoading}>
                    {rotating
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="ki-outline ki-arrows-circle fs-6 me-2.5"></i>{credCode ? "Rotate Code" : "Issue Code"}</>}
                  </button>
                )}
                <button type="button" className="btn btn-xs btn-light-primary d6-mbtn py-1 px-3 fs-9" onClick={handlePrintQR} disabled={!qrSrc}>
                  <i className="ki-outline ki-printer fs-6 me-2.5"></i> Print Tag
                </button>
              </div>
            </div>

            <div className="d6-card p-4 border border-gray-200 bg-white">
              {credLoading && !credCode ? (
                <div className="d-flex align-items-center gap-3 text-gray-500 fs-8">
                  <span className="spinner-border spinner-border-sm" /> Loading access code…
                </div>
              ) : credCode || qrSrc ? (
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-white p-1.5 border border-gray-200 rounded-3 shadow-xs flex-shrink-0" style={{ width: "80px", height: "80px" }}>
                    <img src={qrSrc} alt="QR Code" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  </div>
                  <div className="d-flex flex-column text-start min-w-0">
                    <span className="text-gray-900 fw-bold fs-8 mb-0.5">Active Gate Pass</span>
                    {credCode && <span className="text-success fw-bold fs-6 letter-spacing-1 text-truncate">{credCode}</span>}
                    <span className="text-gray-500 fs-9 mt-0.5 lh-sm">
                      {credential?.issuedAt ? `Issued ${new Date(credential.issuedAt).toLocaleDateString()} • ` : ""}
                      Gate security scans this code for entry &amp; exit.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="d6m-empty py-2">
                  No gate access code has been issued for this vehicle yet.
                </div>
              )}
            </div>
          </div>

          <div className="text-gray-400 fs-9 text-center pt-3 mt-auto">
            Matilda Estate Traffic Security • 24/7 Monitored
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function DownloadLogModal({ modalRef, cars, onClose }) {
  const { hide } = useBootstrapModal(modalRef);
  const [cols, setCols] = useState({
    licensePlate: true, make: true, model: true, year: true, color: true, assignedDependentsCount: true
  });

  const handleClose = () => {
    hide();
    onClose?.();
  };

  const handleDownload = () => {
    const data = cars.map(c => {
      const row = {};
      if (cols.licensePlate) row["License Plate"] = c.licensePlate;
      if (cols.make) row["Make"] = c.make;
      if (cols.model) row["Model"] = c.model;
      if (cols.year) row["Year"] = c.year;
      if (cols.color) row["Colour"] = c.color;
      if (cols.assignedDependentsCount) row["Dependants"] = c.assignedDependentsCount || 0;
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, "Zangon_Vehicles_Log.xlsx");
    toast.success("Download started.");
    handleClose();
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Vehicle Log"
      onClose={handleClose}
      width={520}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload}>
            <i className="ki-outline ki-file-down fs-5 me-2.5 text-white" />
            Download Export
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-4">
        <div className="d6m-muted">
          Select the data fields to include in your exported vehicle registry spreadsheet:
        </div>
        <div className="row g-3">
          {Object.keys(cols).map(k => (
            <div className="col-6" key={k}>
              <label className="d6-card p-3 d-flex align-items-center border border-gray-200 cursor-pointer hover-border-success transition-all m-0">
                <input 
                  className="form-check-input h-18px w-18px" 
                  type="checkbox" 
                  checked={cols[k]} 
                  onChange={(e) => setCols(p => ({ ...p, [k]: e.target.checked }))}
                />
                <span className="text-capitalize text-gray-800 fw-semibold ms-2.5 fs-8">
                  {k.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </label>
            </div>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

// ─── Excel preview modal ─────────────────────────────────────
function ExcelImportModal({ modalRef, rows, onImport, onClose, importing, importProgress }) {
  const { hide } = useBootstrapModal(modalRef);
  const handleClose = () => { hide(); onClose?.(); };

  const resolvedRows = useMemo(() => {
    return rows.map((r, i) => {
      const cleanPlate = String(r.licensePlate || "").trim().toUpperCase();
      const isValid = Boolean(r.make && cleanPlate);
      return {
        ...r,
        index: i + 1,
        cleanPlate,
        isValid,
      };
    });
  }, [rows]);

  const validCount = resolvedRows.filter(r => r.isValid).length;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-up"
      title={`Excel Import Preview — ${rows.length} Vehicle${rows.length !== 1 ? "s" : ""}`}
      onClose={handleClose}
      closeDisabled={importing}
      width={760}
      subheader={
        <div className="px-6 py-2 bg-light border-bottom border-gray-100 d-flex align-items-center justify-content-between">
          <span className="fs-8 text-gray-600">
            {validCount} of {rows.length} records ready for import
          </span>
          <StatusChip color={validCount === rows.length ? "#0EA84A" : "#D97706"}>
            {validCount === rows.length ? "All Valid" : "Review Needed"}
          </StatusChip>
        </div>
      }
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={importing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success d6-mbtn"
            onClick={onImport}
            disabled={importing || validCount === 0}
          >
            {importing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Importing…
              </>
            ) : (
              <>
                <i className="ki-outline ki-file-up fs-5 me-2.5 text-white" />
                Import {validCount} Vehicle{validCount !== 1 ? "s" : ""}
              </>
            )}
          </button>
        </>
      }
    >
      <div className="table-responsive" style={{ maxHeight: "50vh" }}>
        <table className="table table-row-dashed table-row-gray-200 align-middle gs-3 gy-2.5">
          <thead>
            <tr className="fw-bold text-gray-500 fs-9 border-0 text-uppercase">
              <th>#</th>
              <th>Make</th>
              <th>Model</th>
              <th>Plate Number</th>
              <th>Year</th>
              <th>Colour</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {resolvedRows.map((r, i) => (
              <tr key={i}>
                <td className="text-gray-400 fs-9">{r.index}</td>
                <td className="text-gray-900 fw-semibold fs-8">{r.make || "—"}</td>
                <td className="text-gray-700 fs-8">{r.model || "—"}</td>
                <td>
                  {r.cleanPlate ? (
                    <span className="badge bg-light text-gray-900 border border-gray-200 fw-bold fs-9">
                      {r.cleanPlate}
                    </span>
                  ) : (
                    <span className="text-danger fs-9">Missing Plate</span>
                  )}
                </td>
                <td className="text-gray-600 fs-9">{r.year || "—"}</td>
                <td className="text-gray-600 fs-9">{r.color || "Standard"}</td>
                <td>
                  {r.isValid ? (
                    <span className="badge bg-light-success text-success fs-9 fw-bold rounded-pill px-2 py-0.5 d-inline-flex align-items-center gap-1">
                      <i className="ki-outline ki-check-circle text-success fs-7" /> Ready
                    </span>
                  ) : (
                    <span className="badge bg-light-danger text-danger fs-9 fw-bold rounded-pill px-2 py-0.5 d-inline-flex align-items-center gap-1">
                      <i className="ki-outline ki-cross-circle text-danger fs-7" /> Incomplete
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importing && (
        <div className="pt-3">
          <div className="progress h-6px rounded-pill">
            <div
              className="progress-bar bg-success"
              style={{ width: `${importProgress}%`, transition: "width .3s" }}
            />
          </div>
          <div className="text-gray-500 fs-9 text-center mt-1.5">{importProgress}% imported</div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Main Component ──────────────────────────────────────────

export default function TenantCars() {
  const dashboardRef = useRef(null);
  const tenantId = getTenantId();
  const [loading, setLoading] = useState(true);
  const [cars, setCars] = useState([]);
  const [deps, setDeps] = useState([]);

  const { setHeaderConfig } = useTenantHeader();

  useEffect(() => {
    setHeaderConfig({
      title: "Car Management",
      breadcrumb: "Car Management",
      actions: null,
    });
    return () => setHeaderConfig({ title: "", breadcrumb: "", actions: null });
  }, [setHeaderConfig]);

  // Modal Refs
  const carModalRef = useRef(null);
  const carDetailModalRef = useRef(null);
  const downloadLogModalRef = useRef(null);
  const excelModalRef = useRef(null);
  const fileRef = useRef(null);

  const carModal = useBootstrapModal(carModalRef);
  const carDetailModal = useBootstrapModal(carDetailModalRef);
  const downloadLogModal = useBootstrapModal(downloadLogModalRef);
  const excelModal = useBootstrapModal(excelModalRef);

  const [editTarget, setEditTarget] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const [xlsxRows,     setXlsxRows]     = useState([]);
  const [importing,    setImporting]    = useState(false);
  const [importPct,    setImportPct]    = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Filters State
  const [search, setSearch] = useState("");
  const [filterMake, setFilterMake] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterColor, setFilterColor] = useState("");

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [cRes, dRes] = await Promise.all([
        getCars(tenantId),
        getDependents({ pageIndex: 0, pageSize: 100, id: tenantId }),
      ]);
      // The list endpoint only returns assignedDependentsCount; the detail endpoint carries assignedDependents.
      const detailed = await Promise.all(
        parseArrayData(cRes).map((c) =>
          getCarById(c.id, tenantId).then((d) => ({ ...c, ...d })).catch(() => c)
        )
      );
      setCars(detailed);
      setDeps(parseArrayData(dRes));
    } catch { 
      toast.error("Failed to load vehicles."); 
    } finally { 
      setLoading(false); 
    }
  }, [tenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (loading) return undefined;
    const context = gsap.context(() => {
      gsap.fromTo(".gsap-hero", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.06, duration: 0.55, ease: "power3.out", delay: 0.05, clearProps: "all" });
      gsap.fromTo(".gsap-card", { opacity: 0, y: 18 }, { opacity: 1, y: 0, stagger: 0.08, duration: 0.55, ease: "power2.out", delay: 0.2, clearProps: "all" });
    }, dashboardRef);
    return () => context.revert();
  }, [loading]);

  // Derived Filter Options
  const makes = useMemo(() => [...new Set(cars.map(c => c.make))].filter(Boolean).sort(), [cars]);
  const years = useMemo(() => [...new Set(cars.map(c => c.year))].filter(Boolean).sort((a,b) => b - a), [cars]);
  const colors = useMemo(() => [...new Set(cars.map(c => c.color))].filter(Boolean).sort(), [cars]);

  // Filter Logic
  const filteredCars = useMemo(() => {
    return cars.filter(c => {
      const searchStr = `${c.make} ${c.model} ${c.licensePlate}`.toLowerCase();
      const matchSearch = search ? searchStr.includes(search.toLowerCase()) : true;
      const matchMake = filterMake ? c.make === filterMake : true;
      const matchYear = filterYear ? String(c.year) === String(filterYear) : true;
      const matchColor = filterColor ? c.color === filterColor : true;
      return matchSearch && matchMake && matchYear && matchColor;
    });
  }, [cars, search, filterMake, filterYear, filterColor]);

  const handleSave = async ({ form, assignDeps = [] }) => {
    const body = {
      manufacturer: toTitleCase(form.manufacturer || form.make),
      make:         toTitleCase(form.make),
      model:        toTitleCase(form.model),
      color:        toTitleCase(form.color),
      licensePlate: form.licensePlate.trim().toUpperCase(),
      year:         Number(form.year),
      imageUrl:     await uploadImageIfNeeded(form.imageUrl, tenantId),
    };

    try {
      let carId;
      if (editTarget) {
        await updateCar(editTarget.id, body, tenantId);
        carId = editTarget.id;
      } else {
        const added = await addCar(body, tenantId);
        // POST /cars returns the new car id as a plain string in `data`.
        carId = typeof added === "string" ? added : added?.id || added?.data?.id;
      }
      
      if (!editTarget && carId) {
        for (const depId of assignDeps) {
          await assignDependentToCar(carId, depId, tenantId);
        }
      }
      
      toast.success(editTarget ? "Vehicle updated." : "Vehicle registered.");
      setEditTarget(null);
      fetchAll();
    } catch (e) { 
      toast.error(e.message); 
      throw e;
    }
  };

  const handleDelete = async (e, car) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!(await confirmDialog({ title: "Remove Vehicle", message: `Remove ${car.make} (${car.licensePlate}) from the registry?`, confirmLabel: "Remove" }))) return;
    try {
      await deleteCar(car.id, tenantId);
      toast.success("Vehicle removed."); 
      fetchAll();
    } catch (e) { 
      toast.error(e.message); 
    }
  };

  // ─── Excel parse ─────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!rows || rows.length === 0) {
          toast.error("The selected Excel file is empty.");
          return;
        }
        const mapped = rows.map(r => {
          const make = String(r.make || r["Make"] || r.manufacturer || r["Manufacturer"] || "").trim();
          const model = String(r.model || r["Model"] || "").trim();
          const licensePlate = String(r.licensePlate || r["License Plate"] || r["Plate Number"] || r["Plate"] || r.plate || r.plateNumber || "").trim().toUpperCase();
          const yearVal = Number(r.year || r["Year"] || new Date().getFullYear());
          const color = String(r.color || r["Color"] || r["Colour"] || r.colour || "").trim();

          return {
            make: toTitleCase(make),
            model: toTitleCase(model),
            licensePlate,
            year: !isNaN(yearVal) && yearVal > 1900 ? yearVal : new Date().getFullYear(),
            color: toTitleCase(color),
          };
        });
        setXlsxRows(mapped);
        excelModal.show();
      } catch (err) {
        toast.error("Could not parse Excel file: " + err.message);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // ─── Bulk import ─────────────────────────────────────────────
  const handleImport = async () => {
    if (!xlsxRows || xlsxRows.length === 0) return;
    setImporting(true);
    setImportPct(0);
    setImportResult(null);

    const results = { ok: 0, fail: 0, errors: [] };

    for (let i = 0; i < xlsxRows.length; i++) {
      const r = xlsxRows[i];
      const cleanPlate = String(r.licensePlate || "").trim().toUpperCase();
      const cleanMake = toTitleCase(r.make);
      const cleanModel = toTitleCase(r.model);
      const cleanColor = toTitleCase(r.color);
      const numYear = Number(r.year) || new Date().getFullYear();

      if (!cleanMake || !cleanPlate) {
        results.fail++;
        results.errors.push(`Row ${i + 1} (${cleanPlate || cleanMake || "Unknown"}): Missing vehicle make or license plate.`);
        setImportPct(Math.round(((i + 1) / xlsxRows.length) * 100));
        continue;
      }

      try {
        const payload = {
          manufacturer: cleanMake,
          make: cleanMake,
          model: cleanModel,
          color: cleanColor,
          licensePlate: cleanPlate,
          year: numYear,
        };

        await addCar(payload, tenantId);
        results.ok++;
      } catch (err) {
        results.fail++;
        results.errors.push(`Row ${i + 1} (${cleanPlate || cleanMake}): ${err.message}`);
      }
      setImportPct(Math.round(((i + 1) / xlsxRows.length) * 100));
    }

    setImporting(false);
    setImportResult(results);
    excelModal.hide();
    fetchAll();

    if (results.fail === 0) {
      toast.success(`Successfully imported all ${results.ok} vehicle${results.ok !== 1 ? "s" : ""}!`);
    } else if (results.ok > 0) {
      toast(`${results.ok} imported, ${results.fail} failed. Check errors for details.`, { icon: "⚠️" });
    } else {
      toast.error(`Import failed: ${results.errors[0] || ""}`);
    }
  };

  const handleDownloadTemplate = useCallback(() => {
    const wsData = [
      ["Make", "Model", "License Plate", "Year", "Color"],
      ["Toyota", "Corolla", "ABC-123-XY", 2022, "Black"],
      ["Honda", "Civic", "KJA-456-ZZ", 2021, "Silver"],
      ["Mercedes-Benz", "C300", "LND-789-AA", 2023, "White"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 10 },
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles_Template");
    XLSX.writeFile(wb, "Vehicles_Import_Template.xlsx");
  }, []);

  const openRegister = () => {
    setEditTarget(null);
    carModal.show();
  };

  const openEdit = (c) => {
    setEditTarget(c);
    carModal.show();
  };

  const openDetail = (c) => {
    setSelectedDetail(c);
    carDetailModal.show();
  };

  const openDownload = () => {
    downloadLogModal.show();
  };

  return (
    <>
      <PagePreloader visible={loading} />
      <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" className="d-none" onChange={handleFile} />

      <div ref={dashboardRef} className="app-container container-fluid py-6 px-6 px-lg-10">

        {/* Stats Section */}
        <div className="d6-kpi-brand p-6 mb-5">
          <div className="row g-4 g-xl-0 align-items-center">
            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-car-2 fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Registered Vehicles</div>
                    <div className="d6-num text-white my-0.5">{cars.length}</div>
                    <div className="text-white text-opacity-70 fs-9">Owned by occupants</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-arrow-up-down fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Today's Traffic</div>
                    <div className="d6-num text-white my-0.5">—</div>
                    <div className="text-white text-opacity-70 fs-9">Entries + exits logged</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 border-end-xl border-white border-opacity-20 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-arrow-down-left fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Vehicles Coming In</div>
                    <div className="d6-num text-white my-0.5">—</div>
                    <div className="text-white text-opacity-70 fs-9">Gate arrivals today</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-xl-3 gsap-hero">
              <div className="px-4 py-2 h-100">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                      <i className="ki-outline ki-arrow-up-right fs-2 text-white"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="text-white text-opacity-80 fs-8 fw-semibold">Vehicles Going Out</div>
                    <div className="d6-num text-white my-0.5">—</div>
                    <div className="text-white text-opacity-70 fs-9">Gate departures today</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cars Directory Card */}
        <div className="d6-card p-6 mb-5 gsap-card">
          <CardHead
            title="All Registered Vehicles"
            subtitle="Search, filter, and manage authorized estate vehicles"
            className="mb-5"
          >
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
              <input 
                type="text"
                className="d6m-input ps-10" 
                style={{ width: 220, minHeight: 40, height: 40, borderRadius: 999 }}
                placeholder="Search plate or make..." 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
              />
            </div>
            
            <select
              className="d6m-input"
              style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }}
              value={filterMake}
              onChange={e => setFilterMake(e.target.value)}
            >
              <option value="">All Makes</option>
              {makes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            
            <select
              className="d6m-input"
              style={{ width: 125, minHeight: 40, height: 40, borderRadius: 999 }}
              value={filterYear}
              onChange={e => setFilterYear(e.target.value)}
            >
              <option value="">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            
            <select
              className="d6m-input"
              style={{ width: 125, minHeight: 40, height: 40, borderRadius: 999 }}
              value={filterColor}
              onChange={e => setFilterColor(e.target.value)}
            >
              <option value="">All Colors</option>
              {colors.map(c => <option key={c} value={c}>{c}</option>)}
            </select>


            <button
              type="button"
              className="btn btn-light-primary d6-mbtn"
              onClick={() => fileRef.current?.click()}
            >
              <i className="ki-outline ki-file-up fs-5 me-2.5" />
              Upload Excel
            </button>
            <button
              type="button"
              className="btn btn-light d6-mbtn"
              onClick={handleDownloadTemplate}
            >
              <i className="ki-outline ki-file-down fs-5 me-2.5" />
              Template
            </button>
            <button
              type="button"
              className="btn btn-success d6-mbtn"
              onClick={openRegister}
            >
              <i className="ki-outline ki-plus fs-5 me-1 text-white"></i>
              <span>Register Car</span>
            </button>
            <button
              type="button"
              className="btn btn-light d6-mbtn"
              onClick={openDownload}
            >
              <i className="ki-outline ki-file-down fs-5 me-1.5"></i>
              <span>Download Log</span>
            </button>
          </CardHead>
          
          <div>
            {importResult && (
              <div className={`alert alert-${importResult.fail === 0 ? "success" : "warning"} d-flex align-items-center p-4 mb-5 rounded-3`}>
                <i className={`ki-outline ki-${importResult.fail === 0 ? "check-circle" : "warning"} fs-2 me-3 text-${importResult.fail === 0 ? "success" : "warning"}`} />
                <div className="fs-8">
                  <strong>{importResult.ok} imported successfully.</strong>
                  {importResult.fail > 0 && <span className="text-danger ms-1">({importResult.fail} failed: {importResult.errors.slice(0, 2).join("; ")})</span>}
                </div>
                <button type="button" className="btn btn-sm btn-light ms-auto d6-mbtn py-1 px-3" onClick={() => setImportResult(null)}>
                  Dismiss
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <span className="spinner-border spinner-border-lg text-success"></span>
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="d6m-empty py-12">
                <i className="ki-outline ki-car fs-3x text-gray-300 d-block mb-3"></i>
                <div className="d6-title text-gray-900 mb-1">No vehicles found</div>
                <div className="fs-8 text-gray-500">No vehicles match your search or filter criteria.</div>
              </div>
            ) : (
              <div className="row g-5">
                {filteredCars.map(c => {
                  const ownerName = c.assignedDependents && c.assignedDependents.length > 0 
                    ? c.assignedDependents[0].fullName 
                    : "Unassigned";

                  return (
                    <div key={c.id} className="col-12 col-md-6 col-xl-4">
                      <div 
                        className="d6-card h-100 d-flex flex-column cursor-pointer border-hover-success transition-all p-5 position-relative overflow-hidden"
                        onClick={() => openDetail(c)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openDetail(c); }}
                      >
                        <div className="position-relative rounded-3 bg-light overflow-hidden d-flex align-items-center justify-content-center border border-gray-200 mb-4" style={{ height: 160 }}>
                          <img 
                            alt={`${c.make} ${c.model}`} 
                            src={c.imageUrl || `https://cdn.imagin.studio/getimage?customer=img&make=${encodeURIComponent(c.make || '')}&modelFamily=${encodeURIComponent(c.model || '')}&modelYear=${c.year}&paintId=${encodeURIComponent(c.color || '')}&angle=23&zoomType=fullscreen`} 
                            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", padding: "10px" }} 
                          />
                          <div className="position-absolute bottom-0 start-0 p-3">
                            <span className="badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">
                              {c.licensePlate}
                            </span>
                          </div>
                        </div>

                        <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                          <div className="min-w-0">
                            <h4 className="d6-title text-gray-900 mb-1 text-truncate">
                              {c.make} {c.model}
                            </h4>
                            <span className="d6m-muted fs-8">
                              {c.year} • {c.color || "Standard"}
                            </span>
                          </div>
                          <StatusChip color="#0EA84A">Registered</StatusChip>
                        </div>

                        <div className="p-3 bg-light rounded-3 mb-4">
                          <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center flex-shrink-0">
                              {(c.assignedDependents || []).length === 0 ? (
                                <Avatar name={ownerName} size={36} />
                              ) : (
                                (c.assignedDependents || []).slice(0, 4).map((a, i) => (
                                  <span
                                    key={a.dependentId || i}
                                    style={{ marginLeft: i > 0 ? -10 : 0, zIndex: 10 - i, borderRadius: "50%", border: "2px solid #f4f4f5", display: "inline-flex" }}
                                    title={a.fullName}
                                  >
                                    <Avatar src={a.imageUrl} name={a.fullName} size={36} />
                                  </span>
                                ))
                              )}
                            </div>
                            <div className="d-flex flex-column min-w-0">
                              <span className="d6m-name fs-8 text-truncate">
                                {(c.assignedDependents || []).length > 1
                                  ? c.assignedDependents.map(a => a.fullName).join(", ")
                                  : ownerName}
                              </span>
                              <span className="d6m-muted fs-9">
                                {(c.assignedDependents || []).length > 1
                                  ? `${c.assignedDependents.length} Assigned Residents`
                                  : (c.assignedDependents || []).length === 1 ? "Assigned Resident" : "No owner linked"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top border-gray-100">
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="d6-icon-btn"
                              title="Edit"
                              onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                            >
                              <i className="ki-outline ki-pencil fs-5 text-gray-700" />
                            </button>
                          </div>
                          <span className="d6-icon-btn" title="View details">
                            <i className="ki-outline ki-arrow-up-right fs-5"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Car Modal */}
      <CarModal
        modalRef={carModalRef}
        mode={editTarget ? "edit" : "add"}
        initial={editTarget}
        deps={deps}
        onSave={handleSave}
        onClose={() => setEditTarget(null)}
      />

      {/* Download Log Modal */}
      <DownloadLogModal
        modalRef={downloadLogModalRef}
        cars={filteredCars} 
        onClose={() => {}} 
      />

      {/* Car Detail Modal */}
      <CarDetailModal
        modalRef={carDetailModalRef}
        car={selectedDetail}
        deps={deps}
        tenantId={tenantId}
        onDelete={handleDelete}
        onClose={(refresh) => { setSelectedDetail(null); if (refresh) fetchAll(); }}
      />

      {/* Excel Import Modal */}
      <ExcelImportModal
        modalRef={excelModalRef}
        rows={xlsxRows}
        onImport={handleImport}
        onClose={() => {}}
        importing={importing}
        importProgress={importPct}
      />
    </>
  );
}
